import { FormGroup, getSymbol, Input, Label, SubmitButton, Switch } from "@xola/ui-kit";
import { FormField, InputField, requiredNumberMoreThanZero, requiredString, SelectField } from "common";
import { isNil } from "lodash";
import React from "react";
import {
    CatalogItem,
    CouponAlgorithm,
    CouponScope,
    CouponTypeCopy,
    DiscountCoupon,
    FeatureRole,
    LineItemType,
    Permission,
    PurchaseRuleAction,
    PurchaseRuleActionObject,
    PurchaseRuleShortcutDiscountOption,
    PurchaseRuleShortcutTemplateCode,
    PurchaseRuleShortcutType,
    SelectionState,
    Tag,
} from "types";
import { boolean, number, object } from "yup";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";
import { ListFilter, SelectionWithName } from "../Dashboard/Filters/ListFilter";
import { TagInputField } from "../Field/TagInputField";
import { ContentLayout } from "../Layout/ContentLayout";
import { CouponEditorProps } from "./CouponEditorProps";
import LimitFormField from "./LimitFormField";
import { ScrollToFieldError } from "@/helpers/validationHelper";
import { useEquipmentResourcesWithTemplates } from "@/hooks/resources/useEquipmentResourcesWithTemplates";
import { useSeller } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useHasEquipment } from "@/hooks/useHasEquipment";
import { useInfiniteCollection } from "@/hooks/useInfiniteCollection";
import { hasFeature } from "@/models/Seller";
import { useLocalizationStore } from "@/stores/localizationStore";

const validationSchema = object().shape({
    name: requiredString(),
    amount: requiredNumberMoreThanZero().test("checkAlgorithm", "cannot be greater than 100%", (value, form) => {
        return form.parent.algorithm === CouponAlgorithm.PERCENT ? value! <= 100 : true;
    }),
    minUnitCount: number().moreThan(0, "must be more than 0"),
    maxUnitCount: number()
        .moreThan(0, "must be more than 0")
        .test("checkMaxCount", "must be more than the minimum guests", (value, form) => {
            return isNil(form.parent.minUnitCount) || isNil(value) ? true : value >= form.parent.minUnitCount;
        }),
    excludeFee: boolean(),
});

interface FormValues {
    name: string;
    amount: number;
    algorithm: CouponAlgorithm;
    scope: CouponScope;
    minUnitCount: string;
    maxUnitCount: string;
    includeAddOns: boolean;
    demographics: SelectionState;
    equipment: SelectionState;
    merchandise: SelectionState;
    excludeTaxFee?: boolean;
    limit: number | null;
    tags: Tag[];
}

const shouldShowIncludeAddOns = (values: FormValues) => {
    const isPerPersonWithoutDemographicRestrictions =
        values.scope === CouponScope.PER_PERSON &&
        values.demographics?.all &&
        !values.maxUnitCount &&
        !values.minUnitCount;
    return (
        values.algorithm === CouponAlgorithm.PERCENT &&
        (values.scope === CouponScope.PER_BOOKING || isPerPersonWithoutDemographicRestrictions)
    );
};

export const DiscountCouponEditor = ({ coupon, isSaving, onSubmit, onCancel }: CouponEditorProps) => {
    const discountCoupon = coupon as DiscountCoupon;
    const seller = useSeller();
    const locale = useLocalizationStore().getLocale();
    const { isGranted } = useAuthorization();
    const canViewMerchandise = isGranted(Permission.MERCHANDISE_VIEW);
    const hasEquipment = useHasEquipment();
    const canSellEquipment =
        hasFeature(seller, FeatureRole.SELLER_INVENTORY) &&
        seller.preferences.inventory.allowEquipmentAsPurchasableUnit;
    const { data: catalogItems } = useInfiniteCollection<CatalogItem>("catalogItems", {
        unitType: "demographic",
        object: "global_catalog_item",
    });
    const equipmentResources = useEquipmentResourcesWithTemplates();

    const demographicsData = catalogItems
        .map((item) => {
            if (item.template?.id) {
                return {
                    id: item.template.id,
                    name: item.name,
                };
            }
        })
        .filter(Boolean);

    const equipmentData = equipmentResources.map((r) => ({ id: r.templateId, name: r.name }));

    const handleSubmit = async (values: FormValues) => {
        onSubmit({ coupon: getCoupon(discountCoupon, values, canViewMerchandise, canSellEquipment) });
    };

    return (
        <XolaFormik
            enableReinitialize
            validateOnChange
            validateOnBlur={false}
            validationSchema={validationSchema}
            initialValues={getFormValues(discountCoupon, demographicsData, equipmentData)}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, values, setFieldValue }) => (
                <ContentLayout isWide isDisabled={!isGranted(Permission.MODIFY_COUPONS) || isSaving}>
                    <ContentLayout.Section
                        title="Discount"
                        description="Discount Coupon codes are the traditional dollar amount or percentage off coupon."
                    >
                        <FormGroup>
                            <Label>Type</Label>
                            <Input disabled value={CouponTypeCopy.discount} />
                        </FormGroup>

                        <FormField name="name" label="Campaign Name">
                            <InputField isRequired placeholder="Coupon name" />
                        </FormField>

                        {coupon?.limit && <LimitFormField />}

                        <div className="flex items-end space-x-2">
                            <div className="relative">
                                <FormField name="amount" label="Terms" className="min-w-24 max-w-32">
                                    <InputField type="text" placeholder="0" className="block" />
                                </FormField>

                                <div className="absolute top-6 right-0 flex items-center">
                                    <FormField name="algorithm">
                                        <SelectField className="h-full rounded-l-none !border-transparent bg-transparent py-0 !px-2 !pr-7 hover:bg-transparent">
                                            <option value={CouponAlgorithm.ABSOLUTE} className="no-translate">
                                                {getSymbol(seller.currency, locale)}
                                            </option>
                                            <option value={CouponAlgorithm.PERCENT}>%</option>
                                        </SelectField>
                                    </FormField>
                                </div>
                            </div>

                            <div className="flex flex-grow items-center">
                                <span className="mb-4 mr-2 leading-p1">off</span>

                                <FormField name="scope">
                                    <SelectField className="w-60 leading-p1">
                                        <option value={CouponScope.PER_BOOKING}>Per Reservation</option>
                                        <option value={CouponScope.PER_PERSON}>Per Purchasable Demographic</option>
                                        {hasEquipment && (
                                            <option value={CouponScope.PER_EQUIPMENT}>Per Purchasable Equipment</option>
                                        )}
                                        {canViewMerchandise && (
                                            <option value={CouponScope.PER_MERCHANDISE}>Per Merchandise</option>
                                        )}
                                    </SelectField>
                                </FormField>
                            </div>

                            <div className="mb-4 flex-1" data-testid="discount-coupon-editor-demographics-button">
                                {values.scope === CouponScope.PER_PERSON ? (
                                    <ListFilter
                                        label="Demographic"
                                        title="Demographics"
                                        fetch={() => ({ data: demographicsData })}
                                        value={values.demographics}
                                        target={DemographicList}
                                        show={{ search: false }}
                                        onChange={(demographics) => {
                                            setFieldValue("demographics", demographics);
                                        }}
                                    />
                                ) : null}
                                {values.scope === CouponScope.PER_EQUIPMENT ? (
                                    <ListFilter
                                        label="Equipment"
                                        title="Equipments"
                                        fetch={() => ({ data: equipmentData })}
                                        value={values.equipment}
                                        target={EquipmentList}
                                        show={{ search: false }}
                                        onChange={(equipment) => {
                                            setFieldValue("equipment", equipment);
                                        }}
                                    />
                                ) : null}
                            </div>
                        </div>
                        {shouldShowIncludeAddOns(values) && (
                            <FormGroup className="space-y-1">
                                <Label>Add-ons</Label>

                                <Switch.Group>
                                    <Switch
                                        data-testid="coupon-description-form-apply-to-add-ons-toggle"
                                        size="medium"
                                        isChecked={values.includeAddOns}
                                        onChange={(enabled: boolean) => {
                                            setFieldValue("includeAddOns", enabled);
                                        }}
                                    />
                                    <Switch.Label direction="right">Discount applies to add-ons</Switch.Label>
                                </Switch.Group>
                            </FormGroup>
                        )}

                        {values.scope === CouponScope.PER_PERSON || values.scope === CouponScope.PER_EQUIPMENT ? (
                            <div className="flex flex-row space-x-2">
                                <FormField name="minUnitCount" label="Minimum item(s) needed to receive discount">
                                    <InputField type="number" />
                                </FormField>

                                <FormField
                                    name="maxUnitCount"
                                    label="Maximum item(s) allowed to receive discount"
                                    className="flex-1"
                                >
                                    <InputField type="number" />
                                </FormField>
                            </div>
                        ) : null}

                        <FormField name="tags" label="Booking Tags">
                            <TagInputField />
                        </FormField>

                        <div>
                            <Switch.Group>
                                <Switch
                                    isChecked={values.excludeTaxFee}
                                    onChange={(value: boolean) => setFieldValue("excludeTaxFee", value)}
                                />

                                <Switch.Label direction="right" className="text-gray-darker">
                                    Remove taxes and fees
                                </Switch.Label>
                            </Switch.Group>

                            <div className="pl-10 text-sm text-gray-dark">
                                If enabled, all taxes and fees will be removed from a booking when a code from this
                                campaign is applied
                            </div>
                        </div>
                    </ContentLayout.Section>

                    <ContentLayout.Footer>
                        <SubmitButton type="submit" isLoading={isSaving} onClick={handleSubmit}>
                            {coupon.id ? "Save" : "Next"}
                        </SubmitButton>

                        <SecondaryButton
                            type="reset"
                            onClick={() => {
                                onCancel();
                            }}
                        >
                            Cancel
                        </SecondaryButton>
                    </ContentLayout.Footer>

                    <ScrollToFieldError />
                </ContentLayout>
            )}
        </XolaFormik>
    );
};

interface DemographicListProps {
    value: SelectionWithName;
    onClick: () => void;
}

const DemographicList = ({ value, onClick }: DemographicListProps) => {
    const count = value.selected.length;
    return (
        <div
            className="h-10 w-60 cursor-pointer rounded border border-gray-light py-2.5 px-3 leading-p1 text-gray-darker"
            onClick={onClick}
        >
            {count === 0 || value.all ? (
                "All Demographics "
            ) : (
                <var data-var="demographicsCount">{count} demographics</var>
            )}
        </div>
    );
};

const EquipmentList = ({ value, onClick }: DemographicListProps) => {
    const count = value.selected.length;
    return (
        <div
            className="h-10 w-60 cursor-pointer rounded border border-gray-light py-2.5 px-3 leading-p1 text-gray-darker"
            onClick={onClick}
        >
            {count === 0 || value.all ? "All Equipments" : <var data-var="equipmentCount">{count} equipments</var>}
        </div>
    );
};

const getCoupon = (
    coupon: DiscountCoupon,
    values: FormValues,
    canViewMerchandise: boolean,
    canSellEquipment: boolean,
): DiscountCoupon => {
    const action = getPurchaseRuleAction(coupon, values, canViewMerchandise, canSellEquipment);

    return {
        ...coupon,
        name: values.name,
        tags: values.tags,
        limit: Number(values?.limit) === 0 ? null : values?.limit,
        excludeTaxFee: values.excludeTaxFee,
        purchaseRule: {
            name: "",
            ...coupon.purchaseRule,
            actions: [action],
            seller: coupon.seller,
        },
    };
};

const buildPerPersonOptions = (
    coupon: DiscountCoupon,
    values: FormValues,
    options: PurchaseRuleShortcutDiscountOption,
): void => {
    if (values.demographics.all) {
        options.templateTypes = [LineItemType.DEMOGRAPHIC];
        if (values.includeAddOns && shouldShowIncludeAddOns(values)) {
            options.templateTypes = [...options.templateTypes, LineItemType.ADDON, LineItemType.PICKUP];
        }
    } else {
        options.templateIds = values.demographics.selected;
    }
    const minUnitCountFromRule = coupon?.purchaseRule?.actions?.[0]?.shortcut?.options?.minUnitCount;
    options.minUnitCount = values.minUnitCount ? Number(values.minUnitCount) : minUnitCountFromRule ? 1 : undefined;
    if (values.maxUnitCount) {
        options.maxUnitCount = Number(values.maxUnitCount);
    }
    if (values.algorithm === CouponAlgorithm.PERCENT && options.templateTypes) {
        options.scope = values.scope;
        if (!values.minUnitCount && !values.maxUnitCount) {
            options.templateTypes.push(LineItemType.PACKAGE_DISCOUNT);
        }
    }
};

const buildPerEquipmentOptions = (
    coupon: DiscountCoupon,
    values: FormValues,
    options: PurchaseRuleShortcutDiscountOption,
): void => {
    if (values.equipment.all) {
        options.templateTypes = [LineItemType.EQUIPMENT];
    } else {
        options.templateIds = values.equipment.selected;
    }
    const minUnitCountFromRule = coupon?.purchaseRule?.actions?.[0]?.shortcut?.options?.minUnitCount;
    options.minUnitCount = values.minUnitCount ? Number(values.minUnitCount) : minUnitCountFromRule ? 1 : undefined;
    if (values.maxUnitCount) {
        options.maxUnitCount = Number(values.maxUnitCount);
    }
    if (values.algorithm === CouponAlgorithm.PERCENT) {
        options.scope = values.scope;
    }
};

const getPurchaseRuleAction = (
    coupon: DiscountCoupon,
    values: FormValues,
    canViewMerchandise: boolean,
    canSellEquipment: boolean,
): PurchaseRuleAction => {
    const typeMap: Record<CouponAlgorithm, Record<CouponScope, PurchaseRuleShortcutType>> = {
        [CouponAlgorithm.ABSOLUTE]: {
            [CouponScope.PER_PERSON]: PurchaseRuleShortcutType.ABSOLUTE_DISCOUNT_PER_UNIT,
            [CouponScope.PER_EQUIPMENT]: PurchaseRuleShortcutType.ABSOLUTE_DISCOUNT_PER_UNIT,
            [CouponScope.PER_BOOKING]: PurchaseRuleShortcutType.ABSOLUTE_PER_PURCHASE,
            [CouponScope.PER_MERCHANDISE]: PurchaseRuleShortcutType.ABSOLUTE_DISCOUNT_PER_UNIT,
        },
        [CouponAlgorithm.PERCENT]: {
            [CouponScope.PER_PERSON]: PurchaseRuleShortcutType.PERCENT,
            [CouponScope.PER_EQUIPMENT]: PurchaseRuleShortcutType.PERCENT,
            [CouponScope.PER_BOOKING]: PurchaseRuleShortcutType.PERCENT,
            [CouponScope.PER_MERCHANDISE]: PurchaseRuleShortcutType.PERCENT,
        },
    };

    const options: PurchaseRuleShortcutDiscountOption = {};
    if (values.scope === CouponScope.PER_PERSON) {
        buildPerPersonOptions(coupon, values, options);
    } else if (values.scope === CouponScope.PER_EQUIPMENT) {
        buildPerEquipmentOptions(coupon, values, options);
    } else if (values.scope === CouponScope.PER_BOOKING && values.algorithm === CouponAlgorithm.PERCENT) {
        options.templateTypes = [
            LineItemType.DEMOGRAPHIC,
            LineItemType.PACKAGE_DISCOUNT,
            LineItemType.FEEABLE_TAXABLE_ADJUSTMENT,
        ];
        options.scope = values.scope;
        if (values.includeAddOns) {
            options.templateTypes = [
                LineItemType.DEMOGRAPHIC,
                LineItemType.ADDON,
                LineItemType.PICKUP,
                LineItemType.PACKAGE_DISCOUNT,
                LineItemType.FEEABLE_TAXABLE_ADJUSTMENT,
            ];
        }
        if (canSellEquipment) {
            options.templateTypes.push(LineItemType.EQUIPMENT);
        }
        if (canViewMerchandise) {
            options.templateTypes.push(LineItemType.WARE);
        }
    } else if (values.scope === CouponScope.PER_MERCHANDISE) {
        options.templateTypes = [LineItemType.WARE];
    }

    const type = typeMap[values.algorithm][values.scope] ?? PurchaseRuleShortcutType.PERCENT;

    const [action] = coupon.purchaseRule?.actions ?? [];

    return {
        ...action,
        object: PurchaseRuleActionObject.UPDATE_LINE_ITEM_ACTION,
        shortcut: { type, amount: Number(values.amount) * -1, options },
    };
};

const getFormValues = (
    coupon: DiscountCoupon,
    demographicsData: ({ id: string; name: string } | undefined)[],
    equipmentData: { id: string; name: string }[],
): FormValues => {
    const [action] = coupon.purchaseRule?.actions ?? [];
    const shortcut = action?.shortcut;

    if (!shortcut) {
        // Coupon is not migrated, just return what we have
        return { ...initialValues, name: coupon.name, amount: coupon.amount };
    }

    const selectedTemplateIds = shortcut?.options?.templateIds ?? [];
    const validSelectedTemplateIds = selectedTemplateIds.filter((id) =>
        demographicsData?.some((item) => item?.id === id),
    );
    let algorithm = CouponAlgorithm.ABSOLUTE;
    let scope = CouponScope.PER_PERSON;
    const templateTypes =
        shortcut?.options && "templateTypes" in shortcut.options && Array.isArray(shortcut.options.templateTypes)
            ? (shortcut.options.templateTypes as string[])
            : [];
    const hasMerchandiseTemplateType = templateTypes.includes(LineItemType.WARE);
    const isOnlyMerchandiseTemplateType = templateTypes.length === 1 && templateTypes[0] === LineItemType.WARE;
    const isOnlyEquipmentTemplateType = templateTypes.length === 1 && templateTypes[0] === LineItemType.EQUIPMENT;

    switch (shortcut.type) {
        case PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT:
        case PurchaseRuleShortcutType.ABSOLUTE_DISCOUNT_PER_UNIT: {
            algorithm = CouponAlgorithm.ABSOLUTE;
            scope = CouponScope.PER_PERSON;
            const hasEquipmentTemplateIds = selectedTemplateIds.some((id) =>
                equipmentData?.some((item) => item?.id === id),
            );
            if (hasMerchandiseTemplateType) {
                scope = CouponScope.PER_MERCHANDISE;
            } else if (templateTypes.includes(LineItemType.EQUIPMENT) || hasEquipmentTemplateIds) {
                scope = CouponScope.PER_EQUIPMENT;
            }
            break;
        }

        case PurchaseRuleShortcutType.ABSOLUTE_PER_PURCHASE: {
            algorithm = CouponAlgorithm.ABSOLUTE;
            scope = CouponScope.PER_BOOKING;
            break;
        }

        case PurchaseRuleShortcutType.PERCENT: {
            algorithm = CouponAlgorithm.PERCENT;
            scope = CouponScope.PER_BOOKING;
            if (
                shortcut?.options &&
                (shortcut.options.templateCodes?.includes(PurchaseRuleShortcutTemplateCode.DEMOGRAPHIC) ||
                    shortcut.options.templateIds ||
                    ("scope" in shortcut.options && shortcut.options.scope === CouponScope.PER_PERSON) ||
                    shortcut.options.minUnitCount ||
                    shortcut.options.maxUnitCount)
            ) {
                scope = CouponScope.PER_PERSON;
            }
            if (
                shortcut?.options &&
                (("scope" in shortcut.options && shortcut.options.scope === CouponScope.PER_EQUIPMENT) ||
                    isOnlyEquipmentTemplateType)
            ) {
                scope = CouponScope.PER_EQUIPMENT;
            }
            // Check for merchandise coupon by scope or templateTypes
            if (
                shortcut?.options &&
                (("scope" in shortcut.options && shortcut.options.scope === CouponScope.PER_MERCHANDISE) ||
                    isOnlyMerchandiseTemplateType)
            ) {
                scope = CouponScope.PER_MERCHANDISE;
            }

            break;
        }

        default:
            break;
    }

    const validSelectedEquipmentIds = selectedTemplateIds.filter((id) =>
        equipmentData?.some((item) => item?.id === id),
    );

    return {
        ...initialValues,
        name: coupon.name,
        amount: Math.abs(shortcut.amount ?? 1),
        algorithm,
        scope,
        // Ensure empty string if null or undefined
        minUnitCount: isNil(shortcut.options?.minUnitCount) ? "" : String(shortcut.options?.minUnitCount),
        maxUnitCount: isNil(shortcut.options?.maxUnitCount) ? "" : String(shortcut.options?.maxUnitCount),
        includeAddOns:
            shortcut.options?.templateCodes?.[0] === PurchaseRuleShortcutTemplateCode.UNIT ||
            (shortcut.options?.templateTypes?.includes(LineItemType.ADDON) ?? false),
        demographics: {
            all: validSelectedTemplateIds.length === 0 || validSelectedTemplateIds.length === demographicsData.length,
            selected: validSelectedTemplateIds,
        },
        equipment: {
            all: validSelectedEquipmentIds.length === 0,
            selected: validSelectedEquipmentIds,
        },
        excludeTaxFee: coupon.excludeTaxFee,
        tags: coupon.tags,
        limit: Number(coupon?.limit) === 0 ? null : coupon?.limit,
    };
};

const initialValues: FormValues = {
    name: "",
    amount: 0,
    algorithm: CouponAlgorithm.ABSOLUTE,
    scope: CouponScope.PER_BOOKING,
    minUnitCount: "",
    maxUnitCount: "",
    includeAddOns: false,
    demographics: { all: true, selected: [] },
    equipment: { all: true, selected: [] },
    merchandise: { all: true, selected: [] },
    excludeTaxFee: false,
    limit: null,
    tags: [],
};
